import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Download } from "lucide-react";
import {
  type ImportBatchResponse,
  type ImportBatchLineResult,
} from "@/lib/accounts";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { KpiTile } from "@/components/accounts/shared";
import { pushToast } from "@/components/Toaster";

type Filter = "all" | "ok" | "error";

export function ImportResultDialog({
  result,
  onClose,
}: {
  result: ImportBatchResponse;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("all");

  // A staggered import only SAVES the account; Telegram is first contacted
  // later, in the background. Those rows are accepted, not yet imported.
  const queuedCount = result.results.filter((r) => r.status === "queued").length;
  const okCount = result.results.filter((r) => r.status === "ok").length;

  const rows = useMemo(() => {
    // "ok" tab = everything that isn't an error (imported OK + staggered-queued).
    if (filter === "ok") return result.results.filter((r) => r.status !== "error");
    if (filter === "error") return result.results.filter((r) => r.status === "error");
    return result.results;
  }, [result.results, filter]);

  const passwords = useMemo(
    () =>
      result.results.filter(
        (r) =>
          (r.twofa_status === "rotated" || r.twofa_status === "set_fresh") &&
          r.twofa_password,
      ),
    [result.results],
  );

  const downloadCsv = () => {
    const header = "source,tg_username,tg_user_id,twofa_password\n";
    const lines = passwords
      .map((r) =>
        [
          r.archive_name,
          r.tg_username ?? "",
          r.tg_user_id ?? "",
          r.twofa_password ?? "",
        ]
          .map((v) => `"${String(v).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");
    const blob = new Blob([header + lines], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "imported-2fa-passwords.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{t("accounts.import.result_title")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className={`grid gap-3 ${queuedCount > 0 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}>
            <KpiTile label={t("accounts.bulk.total")} value={result.total} />
            <KpiTile label={t("accounts.bulk.imported")} value={okCount} tone="success" />
            {queuedCount > 0 && (
              <KpiTile label={t("accounts.bulk.queued_tile")} value={queuedCount} />
            )}
            <KpiTile
              label={t("accounts.bulk.failed")}
              value={result.failed}
              tone={result.failed > 0 ? "danger" : "default"}
            />
          </div>

          {queuedCount > 0 && (
            <p className="text-xs text-fg-muted">{t("accounts.bulk.queued_note")}</p>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)} variant="pill">
              <TabsList>
                <TabsTrigger value="all">
                  {t("accounts.import.filter_all", { count: result.total })}
                </TabsTrigger>
                <TabsTrigger value="ok">
                  {t("accounts.import.filter_ok", { count: result.imported })}
                </TabsTrigger>
                <TabsTrigger value="error">
                  {t("accounts.import.filter_error", { count: result.failed })}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            {passwords.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                iconLeft={<Download size={16} strokeWidth={2} aria-hidden="true" />}
                onClick={downloadCsv}
              >
                {t("accounts.import.download_passwords")}
              </Button>
            )}
          </div>

          <div className="max-h-[45vh] overflow-auto rounded-xl border border-border">
            <Table>
              <THead>
                <TR>
                  <TH>{t("accounts.import.col.source")}</TH>
                  <TH>{t("accounts.bulk.col.status")}</TH>
                  <TH>{t("accounts.bulk.col.account")}</TH>
                  <TH>{t("accounts.import.col.twofa")}</TH>
                  <TH>{t("accounts.bulk.col.reason")}</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((r, i) => (
                  <ImportResultRow key={i} r={r} />
                ))}
              </TBody>
            </Table>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>{t("common.done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportResultRow({ r }: { r: ImportBatchLineResult }) {
  const { t } = useTranslation();

  const copyPwd = async () => {
    if (!r.twofa_password) return;
    try {
      await navigator.clipboard.writeText(r.twofa_password);
      pushToast({ kind: "success", text: t("accounts.copied") });
    } catch {
      pushToast({ kind: "error", text: t("accounts.copy_failed") });
    }
  };

  // Pasted lines come back as `pasted-3` — name them the way the operator
  // typed them, not the way the backend keyed them.
  const pastedLine = /^pasted-(\d+)$/.exec(r.archive_name);
  const source = pastedLine
    ? t("accounts.import.source_pasted", { line: pastedLine[1] })
    : r.archive_name;

  return (
    <TR>
      <TD className="break-all font-mono text-xs text-fg-muted">{source}</TD>
      <TD>
        <StatusBadge
          domain="scrape_job"
          value={r.status === "ok" ? "done" : r.status === "queued" ? "pending" : "failed"}
          label={
            r.status === "ok"
              ? t("accounts.bulk.ok")
              : r.status === "queued"
                ? t("accounts.bulk.queued")
                : t("accounts.bulk.fail")
          }
        />
      </TD>
      <TD>
        {r.tg_username ? (
          <span>
            @{r.tg_username}{" "}
            <span className="font-mono text-xs text-fg-muted">({r.tg_user_id})</span>
          </span>
        ) : (
          <span className="text-fg-muted">—</span>
        )}
      </TD>
      <TD>
        {(r.twofa_status === "rotated" || r.twofa_status === "set_fresh") &&
        r.twofa_password ? (
          <div className="space-y-1">
            <span className="text-xs text-success">
              {t(
                r.twofa_status === "rotated"
                  ? "accounts.import.col.twofa_rotated"
                  : "accounts.import.col.twofa_set_fresh",
              )}
            </span>
            <div className="flex items-center gap-1">
              <code className="select-all break-all font-mono text-xs">
                {r.twofa_password}
              </code>
              <button
                type="button"
                onClick={copyPwd}
                className="shrink-0 text-fg-muted hover:text-fg-primary"
                aria-label={t("accounts.events.copy")}
              >
                <Copy size={13} strokeWidth={2} aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : r.twofa_status === "kept_seller" ? (
          <span className="text-xs text-warning">{t("accounts.import.col.twofa_kept_seller")}</span>
        ) : r.twofa_status === "kept_unknown" ? (
          <span className="text-xs text-warning">{t("accounts.import.col.twofa_kept_unknown")}</span>
        ) : r.twofa_status === "set_fresh_failed" ? (
          <span className="text-xs text-danger">
            {t("accounts.import.col.twofa_set_fresh_failed")}
          </span>
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        )}
      </TD>
      <TD>
        {r.reason ? (
          <div>
            <span className={r.status === "queued" ? "text-fg-muted" : "text-danger"}>
              {/* Backend reason codes are for us; the operator gets a sentence,
                  with the raw code kept underneath for a bug report. */}
              {t(`accounts.import.reason.${r.reason}`, { defaultValue: r.reason })}
            </span>
            {r.detail && (
              <div className="break-all text-xs text-fg-muted">{r.detail}</div>
            )}
          </div>
        ) : (
          <span className="text-fg-muted">—</span>
        )}
      </TD>
    </TR>
  );
}
